/**
 * useDiscoveryDismissal — localStorage-based dismissal for discovery prompts
 *
 * Rules (from user-story.md Decision 7):
 * - "Not now" hides for 7 days via localStorage
 * - After 2 dismissals of the same prompt type, permanently hidden
 * - Each prompt type has its own independent dismissal counter
 */

import { useMemo, useCallback } from "react";

const SEVEN_DAYS_MS = 7 * 24 * 60 * 60 * 1000;

function getStorageKey(promptId: string, suffix: "ts" | "count"): string {
  return `discovery_dismiss_${promptId}_${suffix}`;
}

function safeGetItem(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

function safeSetItem(key: string, value: string): void {
  try {
    localStorage.setItem(key, value);
  } catch {
    // Safari private mode or quota exceeded — silently ignore
  }
}

interface DiscoveryDismissalResult {
  isDismissed: boolean;
  dismiss: () => void;
}

export function useDiscoveryDismissal(promptId: string | null): DiscoveryDismissalResult {
  const isDismissed = useMemo(() => {
    if (!promptId) return true;

    const countStr = safeGetItem(getStorageKey(promptId, "count"));
    const count = countStr ? parseInt(countStr, 10) : 0;

    // Permanently dismissed after 2 dismissals
    if (count >= 2) return true;

    const tsStr = safeGetItem(getStorageKey(promptId, "ts"));
    if (!tsStr) return false;

    const ts = parseInt(tsStr, 10);
    if (isNaN(ts)) return false;

    // Temporarily dismissed if within 7-day cooldown
    return Date.now() - ts < SEVEN_DAYS_MS;
  }, [promptId]);

  const dismiss = useCallback(() => {
    if (!promptId) return;

    safeSetItem(getStorageKey(promptId, "ts"), String(Date.now()));

    const countStr = safeGetItem(getStorageKey(promptId, "count"));
    const count = countStr ? parseInt(countStr, 10) : 0;
    safeSetItem(getStorageKey(promptId, "count"), String(count + 1));
  }, [promptId]);

  return { isDismissed, dismiss };
}
